import { useEffect, useState } from "react";

/**
 * PWA splash screen. Shows the Maison Pilet monogram on the brand ivory
 * (#F6F2EB) — the same colour as manifest.background_color — so the
 * transition from iOS boot → first paint is seamless. Fades out after
 * `duration` ms; unmounts after the CSS transition ends so nothing
 * lingers behind the app.
 *
 * The component only ever renders on the *first mount* of the session
 * (SessionStorage-guarded). Route navigations don't re-trigger it.
 */
export default function SplashScreen({ duration = 900 }) {
  const [visible, setVisible] = useState(() => {
    // Skip the splash on client-side navigations within the same tab session.
    // First tab open (or PWA cold-start) shows it; hot-reload does not.
    if (typeof window === "undefined") return false;
    try {
      if (sessionStorage.getItem("mp-splash-seen") === "1") return false;
      sessionStorage.setItem("mp-splash-seen", "1");
      return true;
    } catch {
      return true; // sessionStorage disabled — still show
    }
  });
  const [fading, setFading] = useState(false);

  useEffect(() => {
    if (!visible) return;
    // Start the fade slightly before we unmount so the transition is visible.
    const t1 = setTimeout(() => setFading(true), duration - 350);
    // Fully unmount after the fade completes.
    const t2 = setTimeout(() => setVisible(false), duration);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [visible, duration]);

  if (!visible) return null;

  return (
    <div
      aria-hidden
      data-testid="pwa-splash"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#F6F2EB",
        opacity: fading ? 0 : 1,
        pointerEvents: fading ? "none" : "auto",
        transition: "opacity 320ms ease-out",
      }}
    >
      <img
        src="/maison-pilet-logo.png"
        alt="Maison Pilet"
        style={{
          maxWidth: "min(38vw, 220px)",
          maxHeight: "min(48vh, 300px)",
          objectFit: "contain",
          // Very subtle fade-in on the mark itself so it feels considered,
          // not just plopped onto the screen.
          animation: "mp-splash-in 640ms ease-out both",
        }}
      />
      {/* Inline keyframes so no CSS file dep is added. */}
      <style>{`
        @keyframes mp-splash-in {
          0%   { opacity: 0; transform: translateY(6px) scale(0.98); }
          100% { opacity: 1; transform: translateY(0)   scale(1);     }
        }
      `}</style>
    </div>
  );
}
